import React, { useMemo, useState } from 'react';
import { ComponentType } from 'react';
import { useData } from '../../store/DataContext';
import { MenuIcon, XIcon, ChevronLeftIcon, ChevronRightIcon, LayoutDashboardIcon, PillIcon, PackageCheckIcon, ClipboardListIcon, TruckIcon, SlidersHorizontalIcon, ArrowLeftRightIcon, CalendarX2Icon, AlertTriangleIcon, BarChart3Icon, HistoryIcon, FileTextIcon } from 'lucide-react';
import { PharmacyOverview } from './PharmacyOverview';
import { InventoryList } from './InventoryList';
import { ReceiveStock } from './ReceiveStock';
import { PurchaseOrders } from './PurchaseOrders';
import { Suppliers } from './Suppliers';
import { StockActions } from './StockActions';
import { ExpiredMedicines } from './ExpiredMedicines';
import { LowStockAlerts } from './LowStockAlerts';
import { InventoryReports } from './InventoryReports';
import { StockMovementHistory } from './StockMovementHistory';
import { PharmacyDispensing } from './PharmacyDispensing';
import { isExpiringWithin, isLowStock, isOutOfStock } from './pharmacyUtils';
export type PharmacySection = 'overview' | 'dispensing' | 'inventory' | 'receive-stock' | 'purchase-orders' | 'suppliers' | 'adjustments' | 'returns' | 'expired' | 'low-stock' | 'reports' | 'movement-history';
type NavItem = {
  id: PharmacySection;
  label: string;
  icon: ComponentType<{
    className?: string;
  }>;
  count?: number;
  tone?: 'red' | 'amber' | 'blue';
};
type NavGroup = {
  label: string;
  items: NavItem[];
};
export function PharmacyModule() {
  const {
    currentUser,
    medicines,
    medicineBatches,
    purchaseOrders
  } = useData();
  const [section, setSection] = useState<PharmacySection>('overview');
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const alerts = useMemo(() => ({
    low: medicines.filter(isLowStock).length,
    out: medicines.filter(isOutOfStock).length,
    expiring: medicineBatches.filter(batch => isExpiringWithin(batch, 90)).length,
    purchaseOrders: purchaseOrders.filter(order => order.status === 'Pending').length
  }), [medicineBatches, medicines, purchaseOrders]);
  if (!currentUser || !['Pharmacy', 'Administrator'].includes(currentUser.role)) return <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center text-red-700" data-id="element-4388">
        You do not have permission to access the Pharmacy workspace.
      </div>;
  const groups: NavGroup[] = [{
    label: 'Workspace',
    items: [{
      id: 'overview',
      label: 'Dashboard',
      icon: LayoutDashboardIcon
    }, {
      id: 'dispensing',
      label: 'Dispensing',
      icon: ClipboardListIcon
    }]
  }, {
    label: 'Inventory',
    items: [{
      id: 'inventory',
      label: 'Pharmacy Inventory',
      icon: PillIcon
    }, {
      id: 'receive-stock',
      label: 'Receive New Stock',
      icon: PackageCheckIcon
    }, {
      id: 'adjustments',
      label: 'Stock Adjustments',
      icon: SlidersHorizontalIcon
    }, {
      id: 'returns',
      label: 'Medicine Returns',
      icon: ArrowLeftRightIcon
    }, {
      id: 'expired',
      label: 'Expired Medicines',
      icon: CalendarX2Icon,
      count: alerts.expiring,
      tone: 'amber'
    }, {
      id: 'low-stock',
      label: 'Low Stock Alerts',
      icon: AlertTriangleIcon,
      count: alerts.low + alerts.out,
      tone: alerts.out ? 'red' : 'amber'
    }]
  }, {
    label: 'Procurement',
    items: [{
      id: 'purchase-orders',
      label: 'Purchase Orders',
      icon: FileTextIcon,
      count: alerts.purchaseOrders,
      tone: 'blue'
    }, {
      id: 'suppliers',
      label: 'Suppliers',
      icon: TruckIcon
    }]
  }, {
    label: 'Insights',
    items: [{
      id: 'reports',
      label: 'Inventory Reports',
      icon: BarChart3Icon
    }, {
      id: 'movement-history',
      label: 'Stock Movement History',
      icon: HistoryIcon
    }]
  }];
  const select = (next: PharmacySection) => {
    setSection(next);
    setMobileOpen(false);
  };
  const content = () => {
    switch (section) {
      case 'overview':
        return <PharmacyOverview onNavigate={select} data-id="element-4389" />;
      case 'dispensing':
        return <PharmacyDispensing onBack={() => select('overview')} data-id="element-4390" />;
      case 'inventory':
        return <InventoryList data-id="element-4391" />;
      case 'receive-stock':
        return <ReceiveStock data-id="element-4392" />;
      case 'purchase-orders':
        return <PurchaseOrders data-id="element-4393" />;
      case 'suppliers':
        return <Suppliers data-id="element-4394" />;
      case 'adjustments':
        return <StockActions mode="adjustment" data-id="element-4395" />;
      case 'returns':
        return <StockActions mode="return" data-id="element-4396" />;
      case 'expired':
        return <ExpiredMedicines data-id="element-4397" />;
      case 'low-stock':
        return <LowStockAlerts data-id="element-4398" />;
      case 'reports':
        return <InventoryReports data-id="element-4399" />;
      case 'movement-history':
        return <StockMovementHistory data-id="element-4400" />;
    }
  };
  const navigation = (mobile = false) => <nav className="space-y-5" aria-label="Pharmacy navigation" data-id="element-4401">
      {groups.map(group => <div key={group.label} data-id="element-4402">
          <p className={`mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 ${collapsed && !mobile ? 'sr-only' : ''}`} data-id="element-4403">
            {group.label}
          </p>
          <div className="space-y-0.5" data-id="element-4404">
            {group.items.map(item => {
          const Icon = item.icon;
          const active = section === item.id;
          return <button key={item.id} type="button" onClick={() => select(item.id)} title={collapsed && !mobile ? item.label : undefined} className={`group flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1 ${active ? 'bg-teal-50 text-teal-800' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950'} ${collapsed && !mobile ? 'justify-center px-2' : ''}`} data-id="element-4405">
                  <Icon className={`h-4 w-4 shrink-0 ${active ? 'text-teal-700' : 'text-slate-400 group-hover:text-slate-600'} ${collapsed && !mobile ? '' : 'mr-3'}`} data-id="element-4406" />
                  {(!collapsed || mobile) && <>
                      <span className="truncate" data-id="element-4407">{item.label}</span>
                      {item.count ? <span className={`ml-auto min-w-5 rounded-full px-1.5 py-0.5 text-center text-[11px] font-bold ${item.tone === 'red' ? 'bg-red-100 text-red-700' : item.tone === 'amber' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-4408">
                          {item.count}
                        </span> : null}
                    </>}
                </button>;
        })}
          </div>
        </div>)}
    </nav>;
  return <div className="-m-4 flex min-h-[calc(100vh-4rem)] bg-slate-50 sm:-m-6 lg:-m-8" data-id="element-4409">
      <aside className={`hidden shrink-0 border-r border-slate-200 bg-white lg:flex lg:flex-col ${collapsed ? 'w-[76px]' : 'w-[264px]'} transition-[width] duration-200`} data-id="element-4410">
        <div className={`border-b border-slate-100 ${collapsed ? 'p-3' : 'p-5'}`} data-id="element-4411">
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`} data-id="element-4412">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50 text-teal-700" data-id="element-4413">
              <PillIcon className="h-5 w-5" data-id="element-4414" />
            </div>
            {!collapsed && <div data-id="element-4415">
                <p className="text-sm font-bold text-slate-900" data-id="element-4416">BMED Care</p>
                <p className="text-xs font-medium text-pink-600" data-id="element-4417">
                  Pharmacy Operations
                </p>
              </div>}
          </div>
          {!collapsed && <span className="mt-4 inline-flex rounded-md border border-teal-100 bg-teal-50 px-2.5 py-1 text-[11px] font-semibold text-teal-800" data-id="element-4418">
              Live inventory ledger
            </span>}
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-5" data-id="element-4419">{navigation()}</div>
        <div className="border-t border-slate-100 p-3" data-id="element-4420">
          <button type="button" onClick={() => setCollapsed(value => !value)} className="flex w-full items-center justify-center rounded-md py-2 text-slate-400 hover:bg-slate-50 hover:text-slate-700" aria-label={collapsed ? 'Expand pharmacy navigation' : 'Collapse pharmacy navigation'} data-id="element-4421">
            {collapsed ? <ChevronRightIcon className="h-4 w-4" data-id="element-4422" /> : <ChevronLeftIcon className="h-4 w-4" data-id="element-4423" />}
          </button>
        </div>
      </aside>
      {mobileOpen && <div className="fixed inset-0 z-50 flex lg:hidden" data-id="element-4424">
          <button type="button" aria-label="Close pharmacy navigation" className="absolute inset-0 bg-slate-950/35" onClick={() => setMobileOpen(false)} data-id="element-4425" />
          <aside className="relative flex h-full w-[280px] flex-col bg-white shadow-2xl" data-id="element-4426">
            <div className="flex items-center justify-between border-b border-slate-100 p-5" data-id="element-4427">
              <div data-id="element-4428">
                <p className="text-sm font-bold text-slate-900" data-id="element-4429">BMED Care</p>
                <p className="text-xs font-medium text-pink-600" data-id="element-4430">
                  Pharmacy Operations
                </p>
              </div>
              <button type="button" onClick={() => setMobileOpen(false)} className="rounded-md p-2 text-slate-500 hover:bg-slate-100" aria-label="Close pharmacy navigation" data-id="element-4431">
                <XIcon className="h-5 w-5" data-id="element-4432" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4" data-id="element-4433">{navigation(true)}</div>
          </aside>
        </div>}
      <section className="min-w-0 flex-1" data-id="element-4434">
        <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 px-4 py-4 backdrop-blur sm:px-6 lg:px-8" data-id="element-4435">
          <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-4" data-id="element-4436">
            <div className="flex min-w-0 items-center gap-3" data-id="element-4437">
              <button type="button" onClick={() => setMobileOpen(true)} className="rounded-md border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 lg:hidden" aria-label="Open pharmacy navigation" data-id="element-4438">
                <MenuIcon className="h-5 w-5" data-id="element-4439" />
              </button>
              <div data-id="element-4440">
                <p className="text-xs font-medium text-slate-500" data-id="element-4441">
                  Pharmacy <span className="mx-1 text-slate-300" data-id="element-4442">/</span>{' '}
                  Operations
                </p>
                <h1 className="mt-0.5 text-lg font-bold text-slate-900 sm:text-xl" data-id="element-4443">
                  {groups.flatMap(group => group.items).find(item => item.id === section)?.label}
                </h1>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700" data-id="element-4444">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" data-id="element-4445" /> Live
              data
            </span>
          </div>
        </header>
        <main className="mx-auto max-w-[1600px] p-4 sm:p-6 lg:p-8" data-id="element-4446">
          {content()}
        </main>
      </section>
    </div>;
}